import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { beneficiaryAPI } from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BookUser, Plus, Loader2, Search, Trash2, CreditCard, Smartphone, UserCircle } from 'lucide-react';
import { toast } from 'sonner';

const Beneficiaries = () => {
  const [loading, setLoading] = useState(true);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    beneficiary_type: 'UPI',
    upi_id: '',
    account_number: '',
    ifsc_code: '',
    account_holder_name: '',
    nickname: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await beneficiaryAPI.list();
      setBeneficiaries(res.data);
    } catch (err) {
      toast.error('Failed to load beneficiaries');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }
    if (formData.beneficiary_type === 'UPI' && !formData.upi_id) {
      toast.error('UPI ID is required');
      return;
    }
    if (formData.beneficiary_type === 'BANK_ACCOUNT' && (!formData.account_number || !formData.ifsc_code)) {
      toast.error('Account number and IFSC are required');
      return;
    }
    
    setCreating(true);
    try {
      await beneficiaryAPI.create(formData);
      toast.success('Beneficiary added');
      setIsCreateOpen(false);
      setFormData({ name: '', beneficiary_type: 'UPI', upi_id: '', account_number: '', ifsc_code: '', account_holder_name: '', nickname: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add beneficiary');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from beneficiaries?`)) return;
    try {
      await beneficiaryAPI.delete(id);
      toast.success('Beneficiary removed');
      fetchData();
    } catch (err) {
      toast.error('Failed to remove beneficiary');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount || 0);
  };

  const filteredBeneficiaries = beneficiaries.filter(b =>
    b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (b.upi_id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (b.account_number || '').includes(searchTerm)
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="beneficiaries-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">Beneficiaries</h1>
            <p className="text-gray-500 mt-1">Manage your payment recipients</p>
          </div>
          
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-900 hover:bg-blue-800 text-gray-900 gap-2" data-testid="add-beneficiary-btn">
                <Plus className="w-4 h-4" /> Add Beneficiary
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200">
              <DialogHeader>
                <DialogTitle className="text-gray-900">Add Beneficiary</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-500">Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Vendor name" className="bg-gray-50 border-gray-300 text-gray-900" data-testid="benef-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">Type</Label>
                  <Select value={formData.beneficiary_type} onValueChange={(val) => setFormData({...formData, beneficiary_type: val})}>
                    <SelectTrigger className="bg-gray-50 border-gray-300 text-gray-900"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-gray-50 border-gray-300">
                      <SelectItem value="UPI" className="text-gray-900">UPI</SelectItem>
                      <SelectItem value="BANK_ACCOUNT" className="text-gray-900">Bank Account</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.beneficiary_type === 'UPI' ? (
                  <div className="space-y-2">
                    <Label className="text-gray-500">UPI ID</Label>
                    <Input value={formData.upi_id} onChange={(e) => setFormData({...formData, upi_id: e.target.value})} placeholder="vendor@upi" className="bg-gray-50 border-gray-300 text-gray-900 font-['JetBrains_Mono']" data-testid="upi-input" />
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-500">Account Number</Label>
                        <Input value={formData.account_number} onChange={(e) => setFormData({...formData, account_number: e.target.value})} placeholder="1234567890" className="bg-gray-50 border-gray-300 text-gray-900 font-['JetBrains_Mono']" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-500">IFSC Code</Label>
                        <Input value={formData.ifsc_code} onChange={(e) => setFormData({...formData, ifsc_code: e.target.value.toUpperCase()})} placeholder="SBIN0001234" className="bg-gray-50 border-gray-300 text-gray-900 font-['JetBrains_Mono']" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-500">Account Holder Name</Label>
                      <Input value={formData.account_holder_name} onChange={(e) => setFormData({...formData, account_holder_name: e.target.value})} placeholder="Full name" className="bg-gray-50 border-gray-300 text-gray-900" />
                    </div>
                  </>
                )}
                
                <div className="space-y-2">
                  <Label className="text-gray-500">Nickname (Optional)</Label>
                  <Input value={formData.nickname} onChange={(e) => setFormData({...formData, nickname: e.target.value})} placeholder="For easy reference" className="bg-gray-50 border-gray-300 text-gray-900" />
                </div>
                
                <Button onClick={handleCreate} className="w-full bg-blue-900 hover:bg-blue-800" disabled={creating} data-testid="create-benef-btn">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Add Beneficiary
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search beneficiaries..." className="pl-10 bg-white/50 border-gray-200 text-gray-900" />
        </div>

        {filteredBeneficiaries.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredBeneficiaries.map((b) => (
              <Card key={b.beneficiary_id} className="glass-card">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${b.beneficiary_type === 'UPI' ? 'bg-purple-500/10' : 'bg-blue-500/10'}`}>
                      {b.beneficiary_type === 'UPI' ? <Smartphone className="w-5 h-5 text-purple-500" /> : <CreditCard className="w-5 h-5 text-blue-500" />}
                    </div>
                    <Badge className={b.is_blacklisted ? 'status-error' : 'status-success'}>
                      {b.is_blacklisted ? 'Blacklisted' : 'Active'}
                    </Badge>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{b.name}</h3>
                  {b.nickname && <p className="text-sm text-gray-500 mb-2">{b.nickname}</p>}
                  
                  <p className="font-['JetBrains_Mono'] text-sm text-gray-500 mb-4">
                    {b.upi_id || `${b.account_number} (${b.ifsc_code})`}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm mb-4">
                    <span className="text-gray-500">{b.payment_count} payments</span>
                    <span className="text-gray-900 font-['JetBrains_Mono']">{formatCurrency(b.total_amount_sent)}</span>
                  </div>
                  
                  {b.created_by_name && (
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-4 border-t border-gray-100 pt-3">
                      <UserCircle className="w-3.5 h-3.5" />
                      <span>Added by {b.created_by_name}</span>
                    </div>
                  )}
                  
                  <Button variant="outline" size="sm" onClick={() => handleDelete(b.beneficiary_id, b.name)} className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4 mr-2" /> Remove
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="glass-card">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <BookUser className="w-16 h-16 text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">{searchTerm ? 'No beneficiaries match your search' : 'No beneficiaries added yet'}</p>
              {!searchTerm && (
                <Button onClick={() => setIsCreateOpen(true)} className="bg-blue-900 hover:bg-blue-800 text-gray-900">
                  <Plus className="w-4 h-4 mr-2" /> Add First Beneficiary
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Beneficiaries;
